import React, { useEffect, useState } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { useCart } from '../../hooks/useCart';
import { useNavigate } from 'react-router-dom';
import { getCashbackSettings, calculateCashbackForAmount } from '../../services/settings';
import { auth } from '../../lib/firebase';
import { getWallet, withdraw, creditWallet } from '../../services/wallet';
import { createOrdersSplitBySeller } from '../../services/orders';
import { orderReceivedEmail } from '../../services/email';
import { getUserProfile } from '../../services/auth';

const CartPage: React.FC = () => {
  const { cartItems, removeFromCart, updateQuantity, getTotalPrice, clearCart } = useCart();
  const navigate = useNavigate();

  const [cashbackSettings, setCashbackSettings] = useState<any | null>(null);
  const [walletError, setWalletError] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [buyerForm, setBuyerForm] = useState({ name: '', phone: '', address: '', shareLocation: false, buyerNote: '', coordinates: undefined as undefined | { lat: number; lng: number } });

  useEffect(() => {
    (async () => {
      try {
        const settings = await getCashbackSettings();
        setCashbackSettings(settings);
      } catch {}
    })();
  }, []);

  const formatPrice = (price: number) => new Intl.NumberFormat('en-NG', { style: 'currency', currency: 'NGN' }).format(price);

  const totalCashback = cashbackSettings?.enabled
    ? cartItems.reduce((sum, item) => sum + calculateCashbackForAmount(item.price * item.quantity, cashbackSettings), 0)
    : 0;

  const handleProceed = async () => {
    const user = auth.currentUser;
    if (!user) {
      navigate('/auth/sign-in');
      return;
    }
    setWalletError(null);
    setIsProcessing(true);
    try {
      const total = getTotalPrice();
      const wallet = await getWallet(user.uid);
      if ((wallet?.balance || 0) < total) {
        // Show banner and let user choose to go to wallet
        setWalletError('Not enough funds. Please add money to your wallet.');
        return;
      }

      // Generate payment reference up front
      const paymentReference = `WALLET-${Date.now()}`;

      // Debit buyer wallet (label clearly as purchase)
      await withdraw(user.uid, total, { description: `Purchase - escrow hold (₦${formatPrice(total)})`, reference: paymentReference });

      // Create orders per seller and hold in escrow
      const svcItems = cartItems.map(i => ({
        listingId: String(i.listingId || i.id),
        title: i.title,
        price: i.price,
        image: i.image,
        quantity: i.quantity,
        sellerId: i.sellerId || '',
        sellerName: i.sellerName,
      }));
      const { ordersBySeller } = await createOrdersSplitBySeller({ buyerId: user.uid, cartItems: svcItems, paymentReference, buyerDetails: {
        buyerName: buyerForm.name || user.displayName || undefined,
        buyerPhone: buyerForm.phone || undefined,
        buyerEmail: user.email || undefined,
        deliveryAddress: buyerForm.address || undefined,
        shareLocation: buyerForm.shareLocation || false,
        buyerNote: buyerForm.buyerNote || undefined,
        coordinates: buyerForm.shareLocation ? buyerForm.coordinates : undefined,
      } });
      try {
        const buyerProfile = await getUserProfile(user.uid);
        const buyerName = buyerProfile.fullName || undefined;
        for (const pair of ordersBySeller) {
          const sellerProfile = await getUserProfile(pair.sellerId);
          if (sellerProfile.email) {
            await orderReceivedEmail(sellerProfile.email, pair.orderId, buyerName);
          }
        }
      } catch (e) {
        console.warn('Failed to send order-received emails', e);
      }
      // Apply cashback
      if (cashbackSettings?.enabled) {
        const cashback = calculateCashbackForAmount(total, cashbackSettings);
        if (cashback > 0) {
          await creditWallet(user.uid, cashback, `Cashback on purchase of ${formatPrice(total)}`);
        }
      }

      clearCart();
      navigate('/orders');
    } catch (err) {
      console.error('Cart checkout failed:', err);
      setWalletError('Something went wrong while processing your payment.');
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      <main className="max-w-7xl mx-auto px-4 py-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-6">My Cart</h1>

        {walletError && (
          <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded mb-6">
            {walletError}
            <button onClick={() => navigate('/wallet')} className="ml-2 underline">Go to Wallet</button>
          </div>
        )}

        {cartItems.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm p-12 text-center">
            <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <i className="ri-shopping-bag-line text-3xl text-gray-400"></i>
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">Your cart is empty</h3>
            <p className="text-gray-600 mb-6">Browse the shop and add items to your cart.</p>
            <button onClick={() => navigate('/shop')} className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700">Go to Shop</button>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-4">
              {cartItems.map((item) => (
                <div key={item.id} className="bg-white rounded-lg shadow-sm p-4 flex items-center gap-4">
                  {item.image && (
                    <img src={item.image} alt={item.title} className="w-20 h-20 rounded object-cover" />
                  )}
                  <div className="flex-1">
                    <h3 className="font-medium text-gray-900">{item.title}</h3>
                    <p className="text-blue-600 font-semibold">{formatPrice(item.price)}</p>
                    {cashbackSettings?.enabled && (
                      <p className="text-sm text-teal-700 mt-1">
                        Cashback: {formatPrice(calculateCashbackForAmount(item.price * item.quantity, cashbackSettings))}
                      </p>
                    )}
                    <div className="flex items-center gap-3 mt-2">
                      <label className="text-sm text-gray-600">Qty</label>
                      <input
                        type="number"
                        min={1}
                        value={item.quantity}
                        onChange={(e) => updateQuantity(item.id, parseInt(e.target.value || '1', 10))}
                        className="w-16 border rounded px-2 py-1"
                      />
                    </div>
                  </div>
                  <button onClick={() => removeFromCart(item.id)} className="text-red-600 hover:text-red-700">Remove</button>
                </div>
              ))}
            </div>

            <div className="bg-white rounded-lg shadow-sm p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Summary</h2>
              <div className="flex items-center justify-between mb-2">
                <span className="text-gray-600">Subtotal</span>
                <span className="font-semibold text-gray-900">{formatPrice(getTotalPrice())}</span>
              </div>
              {cashbackSettings?.enabled && (
                <div className="flex items-center justify-between mb-2">
                  <span className="text-gray-600">Estimated Cashback</span>
                  <span className="font-semibold text-teal-700">{formatPrice(totalCashback)}</span>
                </div>
              )}
              <p className="text-sm text-gray-500 mb-4">Shipping and fees calculated at checkout.</p>

               <div className="space-y-3 mb-4">
                 <div>
                   <label className="block text-sm font-medium text-gray-700">Your Name</label>
                   <input
                     type="text"
                     value={buyerForm.name}
                     onChange={(e) => setBuyerForm(prev => ({ ...prev, name: e.target.value }))}
                     className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md"
                   />
                 </div>
                 <div>
                   <label className="block text-sm font-medium text-gray-700">Phone Number</label>
                   <input
                     type="text"
                     value={buyerForm.phone}
                     onChange={(e) => setBuyerForm(prev => ({ ...prev, phone: e.target.value }))}
                     className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md"
                   />
                 </div>
                 <div>
                   <label className="block text-sm font-medium text-gray-700">Delivery Address</label>
                   <input
                     type="text"
                     value={buyerForm.address}
                     onChange={(e) => setBuyerForm(prev => ({ ...prev, address: e.target.value }))}
                     className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md"
                   />
                 </div>
                 <label className="flex items-center text-sm text-gray-700">
                   <input
                     type="checkbox"
                     className="mr-2"
                     checked={buyerForm.shareLocation}
                     onChange={(e) => setBuyerForm(prev => ({ ...prev, shareLocation: e.target.checked }))}
                   />
                   Share my location with the seller for delivery
                 </label>
                 {buyerForm.shareLocation && (
                   <div className="mt-2 flex items-center gap-2">
                     <button
                       type="button"
                       onClick={() => {
                         if (!navigator.geolocation) {
                           alert('Geolocation is not supported');
                           return;
                         }
                         navigator.geolocation.getCurrentPosition(
                           (pos) => {
                             const { latitude, longitude } = pos.coords;
                             setBuyerForm(prev => ({ ...prev, coordinates: { lat: latitude, lng: longitude } }));
                           },
                           (err) => {
                             console.error('Location error', err);
                             alert('Could not get location');
                           },
                           { enableHighAccuracy: true, timeout: 10000 }
                         );
                       }}
                       className="px-3 py-2 bg-blue-600 text-white rounded"
                     >
                       Use my location
                     </button>
                     {buyerForm.coordinates && (
                       <span className="text-sm text-gray-600">
                         {buyerForm.coordinates.lat?.toFixed(5)}, {buyerForm.coordinates.lng?.toFixed(5)}
                       </span>
                     )}
                   </div>
                 )}
                 <div>
                   <label className="block text-sm font-medium text-gray-700">Buyer Note (optional)</label>
                   <textarea
                     placeholder="e.g., leave with roommate"
                     value={buyerForm.buyerNote}
                     onChange={(e) => setBuyerForm(prev => ({ ...prev, buyerNote: e.target.value }))}
                     rows={3}
                     className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md"
                   />
                 </div>
               </div>

               <button onClick={handleProceed} disabled={isProcessing} className="w-full bg-blue-600 text-white py-3 rounded-lg hover:bg-blue-700 disabled:opacity-60">{isProcessing ? 'Processing...' : 'Proceed to Checkout'}</button>
            </div>
          </div>
        )}
      </main>

      <Footer />
    </div>
  );
};

export default CartPage;